import { useState } from 'react'

import { PosReportModel, type ShiftReport } from '@/http/models/PosReport'
import { useBusiness } from '@/contexts/BusinessContext'

import { PageHeader } from '@/components/atoms'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { formatDateTime, signed } from '@/components/features/pos/format'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { usePagedReport } from '@/components/features/pos/usePagedReport'
import { usePosLookups } from '@/components/features/pos/usePosLookups'

/** Per-shift sales by payment method, refunds and cash difference (POS report "Shifts", checklist C26). */
const ShiftReports = () => {
  const { formatCurrency } = useBusiness()
  const lookups = usePosLookups()
  const [branch, setBranch] = useState('')
  const list = usePagedReport<ShiftReport>(PosReportModel.getShiftReports, { branch_id: branch || undefined })
  const money = (n: number) => <span className="tabular-nums">{formatCurrency(n)}</span>

  return (
    <div className="space-y-4">
      <PageHeader title="Shift report" description="Each shift's takings by payment method, refunds and cash difference, as the server last rebuilt them." />
      <PosListCard<ShiftReport>
        {...list}
        onPageChange={list.setPage}
        empty="No shift reports yet."
        filters={<FilterSelect label="Branch" value={branch} onChange={setBranch} allLabel="All branches" options={lookups.branches.map((b) => ({ label: b.name, value: b._id }))} />}
        columns={[
          { label: 'Opened', render: (r) => formatDateTime(r.opened_at) },
          { label: 'Branch', render: (r) => lookups.branchName(r.branch_id), className: 'hidden md:table-cell' },
          { label: 'Terminal', render: (r) => lookups.terminalName(r.terminal_id) },
          { label: 'Orders', render: (r) => <span className="tabular-nums">{r.order_count}</span>, className: 'text-right' },
          { label: 'Cash', render: (r) => money(r.sales.cash), className: 'text-right' },
          { label: 'Card', render: (r) => money(r.sales.card), className: 'hidden text-right lg:table-cell' },
          { label: 'Bank', render: (r) => money(r.sales.bank), className: 'hidden text-right lg:table-cell' },
          { label: 'QR', render: (r) => money(r.sales.qr), className: 'hidden text-right lg:table-cell' },
          { label: 'Credit', render: (r) => money(r.sales.credit), className: 'hidden text-right lg:table-cell' },
          { label: 'Refunds', render: (r) => money(r.refunds), className: 'hidden text-right md:table-cell' },
          { label: 'Expected cash', render: (r) => money(r.expected_cash), className: 'text-right' },
          {
            label: 'Difference',
            render: (r) => <span className={r.discrepancy ? 'tabular-nums text-error' : 'tabular-nums'}>{signed(r.discrepancy, formatCurrency)}</span>,
            className: 'text-right',
          },
        ]}
      />
    </div>
  )
}

export default ShiftReports
